import {
  isCampaignReviewStepReady,
  type CampaignMessageCoverageEntry,
  type CampaignReviewBlockerCode,
  type CampaignStatusName,
} from "@multi-chat/shared";
import type { CampaignDraftStepId } from "./campaign-draft-workflow.js";

const POST_DRAFT_REVIEW_STATUSES = [
  "SCHEDULED",
  "RUNNING",
  "COMPLETED",
  "FAILED",
  "CANCELLED",
] as const satisfies readonly CampaignStatusName[];

export function canOpenCampaignReviewStep(input: {
  status: CampaignStatusName;
  snapshotStale: boolean;
  readyRecipientCount: number;
  messageConfigured: boolean;
}): boolean {
  if ((POST_DRAFT_REVIEW_STATUSES as readonly string[]).includes(input.status)) {
    return true;
  }
  return isCampaignReviewStepReady(input);
}

export function reviewBlockerMessageKey(code: CampaignReviewBlockerCode): string {
  switch (code) {
    case "NOT_DRAFT":
      return "campaigns.review.blockers.notDraft";
    case "AUDIENCE_STALE":
      return "campaigns.review.blockers.audienceStale";
    case "AUDIENCE_EMPTY":
      return "campaigns.review.blockers.audienceEmpty";
    case "MESSAGE_NOT_CONFIGURED":
      return "campaigns.review.blockers.messageNotConfigured";
    case "SENDER_UNAVAILABLE":
      return "campaigns.review.blockers.senderUnavailable";
    case "TEMPLATE_UNAVAILABLE":
      return "campaigns.review.blockers.templateUnavailable";
    case "MAPPING_INCOMPLETE":
      return "campaigns.review.blockers.mappingIncomplete";
    case "TEMPLATE_UNSUPPORTED":
      return "campaigns.review.blockers.templateUnsupported";
    case "TEMPLATE_HEADER_IMAGE_MISSING":
      return "campaigns.review.blockers.templateHeaderImageMissing";
    case "NO_EXECUTABLE_RECIPIENTS":
      return "campaigns.review.blockers.noExecutableRecipients";
    case "INSUFFICIENT_MESSAGE_CREDITS":
      return "campaigns.review.blockers.insufficientMessageCredits";
    default: {
      const _exhaustive: never = code;
      return _exhaustive;
    }
  }
}

export type CampaignReviewChecklistRowId = "audience" | "sender" | "template" | "personalization";
export type CampaignReviewChecklistRowState = "ready" | "warning" | "blocked";

export interface CampaignReviewChecklistRow {
  id: CampaignReviewChecklistRowId;
  state: CampaignReviewChecklistRowState;
  /** i18n key explaining why the row needs attention; null when ready. */
  reasonKey: string | null;
  fixStep: CampaignDraftStepId | null;
  /** Largest per-variable missing count (personalization warnings only). */
  missingRecipients: number;
}

export function reviewChecklistRowForBlocker(
  code: CampaignReviewBlockerCode,
): CampaignReviewChecklistRowId | null {
  switch (code) {
    case "AUDIENCE_STALE":
    case "AUDIENCE_EMPTY":
    case "NO_EXECUTABLE_RECIPIENTS":
      return "audience";
    case "SENDER_UNAVAILABLE":
      return "sender";
    case "MESSAGE_NOT_CONFIGURED":
    case "TEMPLATE_UNSUPPORTED":
    case "TEMPLATE_UNAVAILABLE":
    case "TEMPLATE_HEADER_IMAGE_MISSING":
      return "template";
    case "MAPPING_INCOMPLETE":
      return "personalization";
    case "NOT_DRAFT":
    case "INSUFFICIENT_MESSAGE_CREDITS":
      return null;
    default: {
      const _exhaustive: never = code;
      return _exhaustive;
    }
  }
}

const AUDIENCE_BLOCKER_PRECEDENCE = [
  "AUDIENCE_STALE",
  "AUDIENCE_EMPTY",
  "NO_EXECUTABLE_RECIPIENTS",
] as const satisfies readonly CampaignReviewBlockerCode[];

const TEMPLATE_BLOCKER_PRECEDENCE = [
  "TEMPLATE_UNSUPPORTED",
  "TEMPLATE_UNAVAILABLE",
  "TEMPLATE_HEADER_IMAGE_MISSING",
  "MESSAGE_NOT_CONFIGURED",
] as const satisfies readonly CampaignReviewBlockerCode[];

/**
 * Summarizes the server's review blockers into one reason per checklist row.
 * The server reports cascaded codes (e.g. TEMPLATE_UNAVAILABLE when no sender or
 * template is chosen); precedence keeps each row on its root cause.
 */
export function buildCampaignReviewChecklist(input: {
  blockers: readonly CampaignReviewBlockerCode[];
  coverage: readonly CampaignMessageCoverageEntry[];
  platformEndpointSelected: boolean;
  templateSelected: boolean;
  whatsappLaunchAllowed: boolean;
  contentDraftDirty: boolean;
  messageUnlocked: boolean;
  /** No contacts, groups, or upload in the audience definition. Copy only. */
  audienceSelectionEmpty?: boolean;
}): CampaignReviewChecklistRow[] {
  const codes = new Set(input.blockers);
  const contentFixStep: CampaignDraftStepId | null = input.messageUnlocked ? "message" : null;
  const contentBlocked = (reasonKey: string): Pick<CampaignReviewChecklistRow, "reasonKey" | "fixStep"> =>
    input.messageUnlocked
      ? { reasonKey, fixStep: contentFixStep }
      : { reasonKey: "campaigns.review.checklist.contentLocked", fixStep: null };

  const rows: CampaignReviewChecklistRow[] = [];

  const audienceCode = AUDIENCE_BLOCKER_PRECEDENCE.find((code) => codes.has(code));
  rows.push(
    audienceCode
      ? {
          id: "audience",
          state: "blocked",
          reasonKey:
            input.audienceSelectionEmpty || audienceCode === "AUDIENCE_EMPTY"
              ? "campaigns.review.checklist.selectRecipient"
              : reviewBlockerMessageKey(audienceCode),
          fixStep: "audience",
          missingRecipients: 0,
        }
      : { id: "audience", state: "ready", reasonKey: null, fixStep: null, missingRecipients: 0 },
  );

  if (!input.whatsappLaunchAllowed) {
    rows.push({
      id: "sender",
      state: "blocked",
      reasonKey: "campaigns.errors.platformNotEnabled",
      fixStep: null,
      missingRecipients: 0,
    });
  } else if (codes.has("SENDER_UNAVAILABLE")) {
    rows.push({
      id: "sender",
      state: "blocked",
      ...contentBlocked(
        input.platformEndpointSelected
          ? reviewBlockerMessageKey("SENDER_UNAVAILABLE")
          : "campaigns.review.checklist.senderNotSelected",
      ),
      missingRecipients: 0,
    });
  } else {
    rows.push({ id: "sender", state: "ready", reasonKey: null, fixStep: null, missingRecipients: 0 });
  }

  const templateCode = input.templateSelected
    ? TEMPLATE_BLOCKER_PRECEDENCE.find((code) => {
        if (!codes.has(code)) return false;
        if (code === "TEMPLATE_UNAVAILABLE") return !codes.has("SENDER_UNAVAILABLE");
        if (code === "MESSAGE_NOT_CONFIGURED") {
          return !codes.has("SENDER_UNAVAILABLE") && !codes.has("MAPPING_INCOMPLETE");
        }
        return true;
      })
    : undefined;
  if (!input.templateSelected) {
    rows.push({
      id: "template",
      state: "blocked",
      ...contentBlocked("campaigns.review.checklist.templateNotSelected"),
      missingRecipients: 0,
    });
  } else if (templateCode) {
    rows.push({
      id: "template",
      state: "blocked",
      ...contentBlocked(reviewBlockerMessageKey(templateCode)),
      missingRecipients: 0,
    });
  } else if (input.contentDraftDirty) {
    rows.push({
      id: "template",
      state: "warning",
      reasonKey: "campaigns.review.checklist.contentUnsaved",
      fixStep: contentFixStep,
      missingRecipients: 0,
    });
  } else {
    rows.push({ id: "template", state: "ready", reasonKey: null, fixStep: null, missingRecipients: 0 });
  }

  const mappingIncomplete = codes.has("MAPPING_INCOMPLETE");
  if (input.templateSelected && (input.coverage.length > 0 || mappingIncomplete)) {
    let missingRecipients = 0;
    for (const entry of input.coverage) {
      if (entry.missingCount > missingRecipients) missingRecipients = entry.missingCount;
    }
    if (mappingIncomplete) {
      rows.push({
        id: "personalization",
        state: "blocked",
        ...contentBlocked(reviewBlockerMessageKey("MAPPING_INCOMPLETE")),
        missingRecipients,
      });
    } else if (missingRecipients > 0) {
      rows.push({
        id: "personalization",
        state: "warning",
        reasonKey: "campaigns.review.checklist.personalizationMissing",
        fixStep: contentFixStep,
        missingRecipients,
      });
    } else {
      rows.push({
        id: "personalization",
        state: "ready",
        reasonKey: null,
        fixStep: null,
        missingRecipients: 0,
      });
    }
  }

  return rows;
}

const CHECKLIST_STATE_RANK: Record<CampaignReviewChecklistRowState, number> = {
  ready: 0,
  warning: 1,
  blocked: 2,
};

export interface CampaignReviewContentSummary {
  state: CampaignReviewChecklistRowState;
  /** True while Audience gates Content; the nested checks would only show cascade noise. */
  locked: boolean;
  attentionCount: number;
  items: CampaignReviewChecklistRow[];
}

/** Groups sender, template, and personalization rows under one top-level Content row. */
export function summarizeCampaignReviewContent(
  rows: readonly CampaignReviewChecklistRow[],
  messageUnlocked: boolean,
): CampaignReviewContentSummary {
  const items = rows.filter((row) => row.id !== "audience");
  let state: CampaignReviewChecklistRowState = "ready";
  let attentionCount = 0;
  for (const item of items) {
    if (item.state !== "ready") attentionCount += 1;
    if (CHECKLIST_STATE_RANK[item.state] > CHECKLIST_STATE_RANK[state]) state = item.state;
  }
  const locked = !messageUnlocked && state === "blocked";
  return { state, locked, attentionCount, items };
}

export function formatCampaignLocalScheduleParts(
  localDate: string,
  localTime: string,
  locale: string,
): { date: string; time: string } | null {
  if (!calendarDateIsValid(localDate) || !LOCAL_TIME_RE.test(localTime)) return null;
  const [year, month, day] = localDate.split("-").map(Number);
  if (year == null || month == null || day == null) return null;
  const date = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, month - 1, day, 12)));
  return { date, time: localTime };
}

export function formatCampaignScheduleInstant(
  iso: string,
  timezone: string,
  locale: string,
): string {
  return new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeStyle: "short",
    timeZone: timezone,
  }).format(new Date(iso));
}

export function formatCampaignScheduleInstantParts(
  iso: string,
  timezone: string,
  locale: string,
): { date: string; time: string } {
  const instant = new Date(iso);
  const date = new Intl.DateTimeFormat(locale, {
    dateStyle: "medium",
    timeZone: timezone,
  }).format(instant);
  const time = new Intl.DateTimeFormat(locale, {
    timeStyle: "short",
    timeZone: timezone,
  }).format(instant);
  return { date, time };
}

export type CampaignReadonlyPreviewTimingKind = "schedule" | "started" | "completed" | "status";

export function campaignReadonlyPreviewTiming(input: {
  status: CampaignStatusName;
  scheduledAt: string | null;
  startedAt: string | null;
  completedAt: string | null;
}): CampaignReadonlyPreviewTimingKind {
  switch (input.status) {
    case "SCHEDULED":
      return input.scheduledAt ? "schedule" : "status";
    case "RUNNING":
      return input.startedAt ? "started" : "status";
    case "COMPLETED":
      return input.completedAt ? "completed" : "status";
    case "CANCELLED":
      return input.scheduledAt ? "schedule" : "status";
    case "FAILED":
    case "DRAFT":
      return "status";
    default: {
      const _exhaustive: never = input.status;
      return _exhaustive;
    }
  }
}

const LOCAL_DATE_RE = /^\d{4}-\d{2}-\d{2}$/;
const LOCAL_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

export type CampaignScheduleUiIssue = "empty" | "invalid" | "past";

function calendarDateIsValid(localDate: string): boolean {
  if (!LOCAL_DATE_RE.test(localDate)) return false;
  const [year, month, day] = localDate.split("-").map(Number);
  if (year == null || month == null || day == null) return false;
  const utc = new Date(Date.UTC(year, month - 1, day));
  return (
    utc.getUTCFullYear() === year &&
    utc.getUTCMonth() === month - 1 &&
    utc.getUTCDate() === day
  );
}

function zonedYmdHm(now: Date, timeZone: string): string | null {
  try {
    const parts = new Intl.DateTimeFormat("en-US", {
      timeZone,
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const value = (type: Intl.DateTimeFormatPartTypes) =>
      parts.find((part) => part.type === type)?.value ?? "";
    const year = value("year");
    const month = value("month");
    const day = value("day");
    const hour = value("hour");
    const minute = value("minute");
    if (!year || !month || !day || !hour || !minute) return null;
    return `${year}-${month}-${day}T${hour}:${minute}`;
  } catch {
    return null;
  }
}

export function campaignScheduleUiIssue(input: {
  localDate: string;
  localTime: string;
  timezone: string;
  now?: Date;
}): CampaignScheduleUiIssue | null {
  const date = input.localDate.trim();
  const time = input.localTime.trim();
  if (date.length === 0 || time.length === 0) return "empty";
  if (!calendarDateIsValid(date) || !LOCAL_TIME_RE.test(time)) return "invalid";
  const nowStamp = zonedYmdHm(input.now ?? new Date(), input.timezone);
  if (nowStamp == null) return "invalid";
  if (`${date}T${time}` <= nowStamp) return "past";
  return null;
}

export function formatCampaignLocalDateTime(
  localDate: string,
  localTime: string,
  locale: string,
): string | null {
  const parts = formatCampaignLocalScheduleParts(localDate, localTime, locale);
  return parts ? `${parts.date} · ${parts.time}` : null;
}
